import { createContext, useEffect } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useDialogs } from '@toolpad/core/useDialogs';
import { useNavigate } from 'react-router';

import {
  getStudentLedgerOptions,
  getStudentOptions,
  getStudentScheduleOptions,
  listStudentCourseEnrollmentsOptions,
} from '@drivingdoc/api-client/generated/react-query';
import { GetStudentResponse, LedgerEntry, StudentCourseEnrollment, StudentPeriodEnrollment } from 'types/students';

import useAuthContext from 'hooks/useAuthContext';

import { formatCents } from 'utils/formatCurrency';

interface IStudentContext {
  loadingStudent: boolean;
  student?: GetStudentResponse | null;
  loadingCourseEnrollments: boolean;
  courseEnrollments: StudentCourseEnrollment[];
  loadingPeriodEnrollments: boolean;
  periodEnrollments: StudentPeriodEnrollment[];
  loadingLedger: boolean;
  ledger?: LedgerEntry[] | null;
}

// create context with default values
const StudentContext = createContext<IStudentContext>({
  loadingStudent: true,
  student: null,
  loadingCourseEnrollments: true,
  courseEnrollments: [],
  loadingPeriodEnrollments: true,
  periodEnrollments: [],
  loadingLedger: true,
  ledger: [],
});

interface PropTypes {
  children: JSX.Element;
}

function StudentContextProvider({ children }: PropTypes) {
  // get the student id from the auth context
  const { userId: studentId } = useAuthContext();

  const dialogs = useDialogs();
  const navigate = useNavigate();

  // fetch student information
  const { data: studentData, isLoading: loadingStudent } = useQuery({
    ...getStudentOptions({
      path: {
        id: studentId ?? '',
      },
    }),
    enabled: !!studentId,
  });
  const student = studentData as GetStudentResponse | undefined;

  // fetch student course enrollments
  const { data: courseEnrollmentsData, isLoading: loadingCourseEnrollments } = useQuery({
    ...listStudentCourseEnrollmentsOptions({
      path: {
        id: studentId ?? '',
      },
    }),
    enabled: !!studentId,
  });
  const courseEnrollments = courseEnrollmentsData as StudentCourseEnrollment[] | undefined;

  // fetch student period enrollments
  const { data: periodEnrollmentsData, isLoading: loadingPeriodEnrollments } = useQuery({
    ...getStudentScheduleOptions({
      path: {
        id: studentId ?? '',
      },
    }),
    enabled: !!studentId,
  });
  const periodEnrollments = periodEnrollmentsData as StudentPeriodEnrollment[] | undefined;

  const { data: ledgerData, isLoading: loadingLedger } = useQuery({
    ...getStudentLedgerOptions({
      path: {
        id: studentId ?? '',
      },
      query: {
        showVoided: false,
      },
    }),
    enabled: !!studentId,
  });
  const ledger = ledgerData as LedgerEntry[] | undefined;

  useEffect(() => {
    const balance = Number(ledger?.[ledger.length - 1]?.balance ?? 0);
    if (balance > 0) {
      const studentBalance = formatCents(balance);

      // alert user of the non-zero balance with an option to view the ledger
      dialogs
        .confirm(`You have a balance of ${studentBalance}`, {
          okText: 'View Ledger',
          cancelText: 'Dismiss',
        })
        .then((confirmed) => {
          if (confirmed) {
            navigate('/student/ledger');
          }
        });
      console.log(`You have a balance of ${studentBalance}`);
    }
  }, [ledger, dialogs, navigate]);

  return (
    <StudentContext.Provider
      value={{
        loadingStudent: loadingStudent,
        student: student,
        loadingCourseEnrollments,
        courseEnrollments: courseEnrollments ?? [],
        loadingPeriodEnrollments,
        periodEnrollments: periodEnrollments ?? [],
        loadingLedger,
        ledger: ledger ?? [],
      }}
    >
      {children}
    </StudentContext.Provider>
  );
}

export type { IStudentContext };
export { StudentContext, StudentContextProvider };
